import React, { useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { ProfilePicture, Tooltip } from '@getro/rombo';
import { Box, Flex } from 'rebass/styled-components';

import { companiesApi } from 'services/companies';
import { hiresApi } from 'services/hires';
import { networkV2Api } from 'services/networkV2';
import { networkApi } from 'services/network';
import { Plus } from 'lucide-react';
import { networksSelector } from '../../../redux/selectors';
import { contactsApi } from '../../../services/contacts';
import { matchesApi } from '../../../services/matches';
import { userApi2 } from '../../../services/userV2';
import { networkAdminsApi } from '../../../services/networkAdmins';
import { networkAdminsApiV2 } from '../../../services/networkAdminV2';
import { UserAndHelpMenu } from '../userAndHelpMenu';
import { trackEvent } from '../../../helpers/analytics';

export const NetworkNav = () => {
  const { networks, active } = useSelector(networksSelector);
  const dispatch = useDispatch();
  const { url: jobBoardUrl, logoUrl: logo, isManager, ...rest } = networks[active];
  const networkList = Object.values(networks).sort((n1, n2) => {
    const n1Label = n1.name || n1.domain;
    const n2Label = n2.name || n2.domain;

    return n1Label.localeCompare(n2Label);
  });
  const onClickNetwork = () => {
    dispatch(contactsApi.util.resetApiState());
    dispatch(companiesApi.util.resetApiState());
    dispatch(matchesApi.util.resetApiState());
    dispatch(networkAdminsApi.util.resetApiState());
    dispatch(networkAdminsApiV2.util.resetApiState());
    dispatch(hiresApi.util.resetApiState());
    dispatch(networkApi.util.resetApiState());
    dispatch(networkV2Api.util.resetApiState());
    dispatch(userApi2.util.resetApiState());
  };
  const isMenuOpen = useSelector((state) => state.mainMenu.isOpen);
  const scrollableBoxRef = useRef(null);

  useEffect(() => {
    const scrollableBox = scrollableBoxRef.current;
    const handleScroll = () => {
      sessionStorage.setItem('networkNavScrollPosition', scrollableBox.scrollTop.toString());
    };

    if (scrollableBox) {
      const scrollPosition = sessionStorage.getItem('networkNavScrollPosition');
      if (scrollPosition) {
        scrollableBox.scrollTop = parseInt(scrollPosition, 10);
      }

      scrollableBox.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (scrollableBox) {
        scrollableBox.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        bottom: 0,
        zIndex: 100,
        bg: 'neutral.0',
        borderRight: '1px solid',
        borderRightColor: 'neutral.100',
        display: isMenuOpen ? 'block' : ['none', 'block'],
        height: '100vh',
        transition: 'transform 0.30s ease-in-out',
      }}
    >
      <Box
        maxWidth="48px"
        sx={{
          overflow: 'scroll',
          height: 'calc(100vh - 80px)',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          px: '8px',
          py: '8px',
          top: 0,
          bottom: 0,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
        ref={scrollableBoxRef}
      >
        {networkList.map(({ id, slug, name, domain, logoUrl }) => (
          <Tooltip
            key={id}
            placement="right"
            content={name}
            sx={{
              width: 'max-content',
              p: '4px 8px',
            }}
            trigger={
              <Flex
                key={id}
                as={NavLink}
                to={`/networks/${slug}`}
                height="32px"
                alignItems="center"
                sx={{
                  background: 'none',
                  textDecoration: 'none',
                  transition: 'all ease 0.3s',
                  boxShadow:
                    rest.id === id
                      ? '0px 0px 0px 2px #F7FAFC, 0px 0px 0px 4px #6D33FF'
                      : ' 0px 0px 0px 2px #FFFFFF, 0px 0px 0px 3px #DEE4ED',
                  borderRadius: '4px',
                  ':hover': {
                    bg: 'purple.100',
                    boxShadow:
                      rest.id === id
                        ? '0px 0px 0px 2px #F7FAFC, 0px 0px 0px 4px #6D33FF'
                        : '0px 0px 0px 2px #F7FAFC, 0px 0px 0px 4px #8A75F0',
                  },
                  '& > svg': {
                    color: 'purple.500',
                  },
                }}
                onClick={onClickNetwork}
                justifyContent="space-between"
              >
                <Flex alignItems="center">
                  <Box borderRadius="4px" width="32px" height="32px" minWidth="32px" minHeight="32px">
                    <ProfilePicture
                      imageUrl={logoUrl}
                      name={name || domain}
                      variant="square"
                      width="32px"
                      height="32px"
                      minWidth="32px"
                      minHeight="32px"
                    />
                  </Box>
                </Flex>
              </Flex>
            }
          />
        ))}
        <Tooltip
          placement="right"
          content="Create network"
          sx={{
            width: 'max-content',
            p: '4px 8px',
          }}
          trigger={
            <Box
              sx={{
                cursor: 'pointer',
                bg: 'neutral.50',
                borderRadius: '4px',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '32px',
                minHeight: '32px',
                display: 'flex',
              }}
              as="a"
              href=" https://www.getro.com/demo"
              rel="noopener noreferrer"
              target="_blank"
              onClick={() => {
                trackEvent('create_network_click', {
                  network_id: active,
                  origin: 'network_selector',
                });
              }}
            >
              <Box as={Plus} size="16px" />
            </Box>
          }
        />
      </Box>

      <Box
        sx={{
          position: 'fixed',
          height: '80px',
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          width: '48px',
          gap: '8px',
        }}
      >
        <UserAndHelpMenu
          minimal
          helpTooltipContent="Help and support"
          myAccountTooltipContent="My account"
          profilePictureSize="32px"
        />
      </Box>
    </Box>
  );
};
